import React, { useEffect, useMemo } from 'react'
import styled, { useTheme } from 'styled-components'
import {
    Background,
    BackgroundVariant,
    BuiltInEdge,
    Handle,
    MarkerType,
    Node,
    NodeProps,
    Position,
    ReactFlow,
    useNodesState,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import useBoundingClientRect from '../hooks/useBoundingClientRect'
import { PROJECT_THEME } from '../theme'

const { COLOUR_LIGHT, COLOUR_MID_LIGHT, COLOUR_MID, COLOUR_MID_DARK, COLOUR_DARK, OFF_WHITE, DARK_GREY, LIGHT_GREY } = PROJECT_THEME.colour;

export type NodeStyle = 'solid' | 'outline' | 'pill';
export type Palette = 'lane' | 'amber' | 'gradient' | 'mono';

export type ProcessFlowConfig = {
    label: string;
    nodeStyle: NodeStyle;
    palette: Palette;
    animated: boolean;
    arrows: boolean;
    background: 'dots' | 'lines' | 'none';
    /* chip font size at scale 1 — the size the graph was hand-placed at */
    fontSize: number;
};

type Stage = { id: string; label: string; lane: string };

const LANE_COLOURS: Record<string, string> = {
    Engage: COLOUR_LIGHT,
    Design: COLOUR_MID_LIGHT,
    Build: COLOUR_MID,
    Verify: COLOUR_MID_DARK,
    Operate: COLOUR_DARK,
};

const STAGES: Stage[] = [
    { id: 'enquiry', label: 'Enquiry', lane: 'Engage' },
    { id: 'discovery', label: 'Discovery', lane: 'Engage' },
    { id: 'success-criteria', label: 'Success Criteria', lane: 'Engage' },
    { id: 'proposal', label: 'Proposal', lane: 'Engage' },

    { id: 'system-design', label: 'System Design', lane: 'Design' },
    { id: 'data-model', label: 'Data Model', lane: 'Design' },
    { id: 'api-contract', label: 'API Contract', lane: 'Design' },
    { id: 'threat-model', label: 'Threat Model', lane: 'Design' },
    { id: 'capacity-model', label: 'Capacity & Cost Model', lane: 'Design' },

    { id: 'implementation', label: 'Implementation', lane: 'Build' },
    { id: 'code-review', label: 'Code Review', lane: 'Build' },
    { id: 'infrastructure', label: 'Infrastructure as Code', lane: 'Build' },
    { id: 'documentation', label: 'Documentation', lane: 'Build' },

    { id: 'unit-tests', label: 'Unit & Integration Tests', lane: 'Verify' },
    { id: 'preview-environments', label: 'Preview Environments', lane: 'Verify' },
    { id: 'qa', label: 'QA & Validation', lane: 'Verify' },
    { id: 'load-testing', label: 'Performance & Load Testing', lane: 'Verify' },

    { id: 'release', label: 'Release', lane: 'Operate' },
    { id: 'canary', label: 'Canary Rollout', lane: 'Operate' },
    { id: 'observability', label: 'Observability', lane: 'Operate' },
    { id: 'disaster-recovery', label: 'Disaster Recovery', lane: 'Operate' },
];

const STAGE_BY_ID: Record<string, Stage> = Object.fromEntries(STAGES.map((s) => [s.id, s]));

/* Each pair reads "the left must be settled before the right means anything", not "the left comes first in a list". */
const DEPENDENCIES: Array<[string, string]> = [
    ['enquiry', 'discovery'],
    ['discovery', 'success-criteria'],
    ['discovery', 'proposal'],
    ['success-criteria', 'proposal'],
    ['proposal', 'system-design'],
    ['system-design', 'data-model'],
    ['system-design', 'api-contract'],
    ['system-design', 'threat-model'],
    ['system-design', 'capacity-model'],
    ['data-model', 'api-contract'],
    ['data-model', 'disaster-recovery'],
    ['api-contract', 'implementation'],
    ['api-contract', 'unit-tests'],
    ['api-contract', 'documentation'],
    ['threat-model', 'code-review'],
    ['threat-model', 'infrastructure'],
    ['capacity-model', 'infrastructure'],
    ['implementation', 'code-review'],
    ['implementation', 'unit-tests'],
    ['implementation', 'documentation'],
    ['infrastructure', 'preview-environments'],
    ['infrastructure', 'release'],
    ['infrastructure', 'disaster-recovery'],
    ['documentation', 'disaster-recovery'],
    ['unit-tests', 'release'],
    ['preview-environments', 'qa'],
    ['preview-environments', 'load-testing'],
    ['qa', 'release'],
    ['load-testing', 'release'],
    ['release', 'canary'],
    ['canary', 'observability'],
];

/* Edges that run backwards: what a strictly-ordered chain can't show. Drawn dashed, off-white. */
const FEEDBACK: Array<[string, string]> = [
    ['code-review', 'implementation'],
    ['load-testing', 'capacity-model'],
    ['observability', 'canary'],
    ['observability', 'success-criteria'],
    ['observability', 'implementation'],
    ['disaster-recovery', 'infrastructure'],
];

/*
 * Hand-placed centres, not a formula — System Design fans out to four artifacts, Release converges
 * four independent inputs, Observability loops back. Units are arbitrary; DESIGN below measures the
 * real chip footprints against them once, and buildGraph scales the whole thing to fit any container.
 */
const RAW_POSITIONS: Record<string, { x: number; y: number }> = {
    'enquiry': { x: 126, y: 70 },
    'discovery': { x: 126, y: 182 },
    'success-criteria': { x: 126, y: 301 },
    'proposal': { x: 126, y: 420 },

    'system-design': { x: 413, y: 301 },

    'data-model': { x: 679, y: 84 },
    'api-contract': { x: 679, y: 210 },
    'threat-model': { x: 679, y: 343 },
    'capacity-model': { x: 679, y: 476 },

    'documentation': { x: 945, y: 77 },
    'implementation': { x: 945, y: 210 },
    'code-review': { x: 945, y: 343 },
    'infrastructure': { x: 945, y: 566 },

    'unit-tests': { x: 1211, y: 210 },
    'preview-environments': { x: 1211, y: 490 },
    'qa': { x: 1428, y: 399 },
    'load-testing': { x: 1428, y: 567 },

    'release': { x: 1064, y: 707 },
    'canary': { x: 1274, y: 707 },
    'observability': { x: 1442, y: 819 },
    'disaster-recovery': { x: 777, y: 819 },
};

const DESIGN_PAD = 24;
const MAX_POSITION_SCALE = 1.6;
const MIN_FONT = 10;
const MIN_EDGE = 8;

/* JetBrains Mono advances 0.6em, plus the 0.02em tracking below — lets the layout size chips without measuring the DOM. */
const CHAR_ADVANCE = 0.62;
const CHIP_PAD_X = 15;
const CHIP_PAD_Y = 9;

const chipWidth = (label: string, fontSize: number) =>
    Math.ceil(label.length * fontSize * CHAR_ADVANCE) + 2 * CHIP_PAD_X;

const chipHeight = (fontSize: number) => Math.ceil(fontSize * 1.3) + 2 * CHIP_PAD_Y;

const StageBox = styled.div<{
    $style: NodeStyle;
    $colour: string;
    $text: string;
    $fontSize: number;
}>`
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    white-space: nowrap;
    padding: ${CHIP_PAD_Y}px ${CHIP_PAD_X}px;
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    font-size: ${({ $fontSize }) => $fontSize}px;
    line-height: 1.3;
    letter-spacing: 0.02em;
    color: ${({ $text }) => $text};

    ${({ $style, $colour }) => {
        switch ($style) {
            case 'outline':
                return `
                    background: transparent;
                    border: 1.5px solid ${$colour};
                    border-radius: 4px;
                `;
            case 'pill':
                return `
                    background: ${$colour};
                    border-radius: 999px;
                `;
            default:
                return `
                    background: ${$colour};
                    border-radius: 4px;
                `;
        }
    }}
`;

const HANDLE_STYLE: React.CSSProperties = { opacity: 0, width: 1, height: 1, minWidth: 1, minHeight: 1, border: 'none' };

type StageNodeData = {
    label: string;
    colour: string;
    text: string;
    nodeStyle: NodeStyle;
    fontSize: number;
};

function StageNode({ data }: NodeProps<Node<StageNodeData>>) {
    return (
        <>
            <Handle type='target' position={Position.Left} id='l' style={HANDLE_STYLE} />
            <Handle type='target' position={Position.Top} id='t' style={HANDLE_STYLE} />
            <Handle type='target' position={Position.Right} id='r' style={HANDLE_STYLE} />
            <Handle type='target' position={Position.Bottom} id='b' style={HANDLE_STYLE} />
            <StageBox data-physics $style={data.nodeStyle} $colour={data.colour} $text={data.text} $fontSize={data.fontSize}>
                <span>{data.label}</span>
            </StageBox>
            <Handle type='source' position={Position.Left} id='sl' style={HANDLE_STYLE} />
            <Handle type='source' position={Position.Top} id='st' style={HANDLE_STYLE} />
            <Handle type='source' position={Position.Right} id='sr' style={HANDLE_STYLE} />
            <Handle type='source' position={Position.Bottom} id='sb' style={HANDLE_STYLE} />
        </>
    );
}

const NODE_TYPES = { stage: StageNode };

const LIGHT_FILLS = new Set([COLOUR_LIGHT, OFF_WHITE]);

function textColour(nodeStyle: NodeStyle, fill: string) {
    const filled = nodeStyle === 'solid' || nodeStyle === 'pill';
    return filled && LIGHT_FILLS.has(fill) ? DARK_GREY : OFF_WHITE;
}

/* Longest dependency path — only used for the optional 'gradient' palette, not for layout. */
function stageDepths() {
    const depth: Record<string, number> = Object.fromEntries(STAGES.map((s) => [s.id, 0]));
    for (let pass = 0; pass < STAGES.length; pass += 1) {
        let moved = false;
        DEPENDENCIES.forEach(([from, to]) => {
            if (depth[to] < depth[from] + 1) {
                depth[to] = depth[from] + 1;
                moved = true;
            }
        });
        if (!moved) break;
    }
    return depth;
}

function paletteColour(config: ProcessFlowConfig, stage: Stage, depth: number, maxDepth: number) {
    switch (config.palette) {
        case 'amber':
            return COLOUR_LIGHT;
        case 'gradient': {
            const ramp = [COLOUR_LIGHT, COLOUR_MID_LIGHT, COLOUR_MID, COLOUR_MID_DARK, COLOUR_DARK];
            return ramp[Math.min(ramp.length - 1, Math.floor((depth / Math.max(1, maxDepth)) * (ramp.length - 1)))];
        }
        case 'mono':
            return OFF_WHITE;
        default:
            return LANE_COLOURS[stage.lane];
    }
}

// The hand-placed bounding box at a given font, measured from the actual chip footprints rather than guessed.
function designMetrics(baseFont: number) {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    STAGES.forEach((stage) => {
        const { x, y } = RAW_POSITIONS[stage.id];
        const w = chipWidth(stage.label, baseFont);
        const h = chipHeight(baseFont);
        minX = Math.min(minX, x - w / 2);
        maxX = Math.max(maxX, x + w / 2);
        minY = Math.min(minY, y - h / 2);
        maxY = Math.max(maxY, y + h / 2);
    });

    return {
        minX,
        minY,
        width: maxX - minX + 2 * DESIGN_PAD,
        height: maxY - minY + 2 * DESIGN_PAD,
    };
}

// Picks whichever handle pair matches the dominant direction between two chip centres, so an edge
// approaches from a real side of the box in any of the four directions the graph actually uses.
function pickHandles(dx: number, dy: number) {
    if (Math.abs(dy) >= Math.abs(dx)) {
        return dy >= 0 ? { source: 'sb', target: 't' } : { source: 'st', target: 'b' };
    }
    return dx >= 0 ? { source: 'sr', target: 'l' } : { source: 'sl', target: 'r' };
}

// True iff every pair of chips clears the other on at least one axis at this font and spacing —
// checked directly against the real footprints rather than inferred from a shared multiplier.
function marginsSafe(fontSize: number, positionScale: number): boolean {
    const w: Record<string, number> = {};
    STAGES.forEach((stage) => { w[stage.id] = chipWidth(stage.label, fontSize); });
    const h = chipHeight(fontSize);

    for (let i = 0; i < STAGES.length; i += 1) {
        for (let j = i + 1; j < STAGES.length; j += 1) {
            const a = STAGES[i].id;
            const b = STAGES[j].id;
            const dx = Math.abs(RAW_POSITIONS[a].x - RAW_POSITIONS[b].x) * positionScale;
            const dy = Math.abs(RAW_POSITIONS[a].y - RAW_POSITIONS[b].y) * positionScale;
            const combinedHalfWidth = (w[a] + w[b]) / 2;
            if (dx < combinedHalfWidth && dy < h) return false;
        }
    }
    return true;
}

// Spacing always compresses freely to fill the container exactly — see buildGraph. Font only steps
// down, one size at a time, once that compression alone would start overlapping chips at the
// preferred size, and only as far as it has to; it never moves just because the container resized.
function chooseFont(positionScale: number, preferredFont: number): number {
    for (let font = preferredFont; font >= MIN_FONT; font -= 1) {
        if (marginsSafe(font, positionScale)) return font;
    }
    return MIN_FONT;
}

/* The hand-placed graph only reads as a graph while its chips clear each other. Once the
   container is too narrow for that even at the smallest font the layout may use, no amount of
   compression helps and the stacked lane list below is rendered instead. */
const graphFits = (config: ProcessFlowConfig, containerWidth: number): boolean => {
    if (containerWidth <= 0) return true;
    const design = designMetrics(config.fontSize);
    const positionScale = Math.min(MAX_POSITION_SCALE, containerWidth / design.width);
    return marginsSafe(MIN_FONT, positionScale);
};

const LANE_ORDER = STAGES.reduce<string[]>((lanes, stage) => (
    lanes.includes(stage.lane) ? lanes : [...lanes, stage.lane]
), []);

type Chip = { x: number; y: number; w: number; h: number };

function buildGraph(config: ProcessFlowConfig, containerWidth: number, ink: string) {
    const design = designMetrics(config.fontSize);
    const width = containerWidth > 0 ? containerWidth : design.width;
    // no lower clamp — spacing always compresses to exactly match the container, so content is never
    // wider than it and nothing needs to scroll. Capped only on the way up, so it doesn't sprawl on
    // very wide screens.
    const positionScale = Math.min(MAX_POSITION_SCALE, width / design.width);
    const fontSize = chooseFont(positionScale, config.fontSize);
    const pad = Math.max(MIN_EDGE, Math.round(DESIGN_PAD * positionScale));

    const depth = stageDepths();
    const maxDepth = Math.max(...Object.values(depth));

    const chips: Record<string, Chip> = {};
    STAGES.forEach((stage) => {
        const raw = RAW_POSITIONS[stage.id];
        const w = chipWidth(stage.label, fontSize);
        const h = chipHeight(fontSize);
        const centreX = (raw.x - design.minX) * positionScale + pad;
        const centreY = (raw.y - design.minY) * positionScale + pad;
        const x = Math.min(width - w - MIN_EDGE, Math.max(MIN_EDGE, centreX - w / 2));
        chips[stage.id] = { x, y: centreY - h / 2, w, h };
    });

    const nodes: Node[] = STAGES.map((stage) => {
        const chip = chips[stage.id];
        const colour = paletteColour(config, stage, depth[stage.id], maxDepth);
        return {
            id: stage.id,
            type: 'stage',
            position: { x: chip.x, y: chip.y },
            width: chip.w,
            height: chip.h,
            data: {
                label: stage.label,
                colour,
                text: textColour(config.nodeStyle, colour),
                nodeStyle: config.nodeStyle,
                fontSize,
            },
            draggable: true,
        };
    });

    const buildEdges = (pairs: Array<[string, string]>, kind: 'dep' | 'back'): BuiltInEdge[] => pairs.map(([from, to]) => {
        const a = chips[from];
        const b = chips[to];
        const dx = (b.x + b.w / 2) - (a.x + a.w / 2);
        const dy = (b.y + b.h / 2) - (a.y + a.h / 2);
        const { source, target } = pickHandles(dx, dy);
        const stroke = kind === 'back'
            ? ink
            : (config.palette === 'mono' ? LIGHT_GREY : LANE_COLOURS[STAGE_BY_ID[to].lane]);

        return {
            id: `${kind}-${from}-${to}`,
            source: from,
            target: to,
            sourceHandle: source,
            targetHandle: target,
            type: 'default',
            /* Only the dashed feedback edges flow — they're the ones that need a direction cue,
               since the solid dependency edges already read left-to-right. */
            animated: kind === 'back' ? true : config.animated,
            style: {
                stroke,
                strokeWidth: kind === 'back' ? 1.6 : 1.3,
                opacity: kind === 'back' ? 0.85 : 0.75,
                strokeDasharray: kind === 'back' ? '6 5' : undefined,
            },
            markerEnd: config.arrows
                ? { type: MarkerType.ArrowClosed, width: 11, height: 11, color: stroke }
                : undefined,
        };
    });

    const edges: BuiltInEdge[] = [...buildEdges(DEPENDENCIES, 'dep'), ...buildEdges(FEEDBACK, 'back')];

    return { nodes, edges, height: Math.ceil(design.height * positionScale) };
}

export const PROCESS_FLOW_CONFIG: ProcessFlowConfig = {
    label: 'process',
    nodeStyle: 'solid',
    palette: 'lane',
    animated: false,
    arrows: true,
    background: 'none',
    fontSize: 18,
};

const Layout = styled.div`
    width: 100%;
    position: relative;
    z-index: 3;
`;

// no overflow-x: auto — buildGraph guarantees the frame never exceeds this width, so there is
// nothing to scroll. Kept only as the ref target width measurement runs against.
const Measured = styled.div`
    width: 100%;
`;

const Frame = styled.div<{ $height: number }>`
    width: 100%;
    height: ${({ $height }) => $height}px;
    position: relative;

    .react-flow__attribution {
        display: none;
    }
`;

const LaneList = styled.ol`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 1.4rem;
`;

const Lane = styled.li`
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
`;

const LaneName = styled.h3<{ $colour: string }>`
    display: flex;
    align-items: center;
    gap: 0.5em;
    margin: 0;
    font-family: ${({ theme }) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: ${({ $colour }) => $colour};

    &::before {
        content: '';
        width: 0.62em;
        height: 0.62em;
        flex: none;
        background-color: ${({ $colour }) => $colour};
    }
`;

const LaneStages = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
`;

const StageChip = styled.span<{ $colour: string; $text: string }>`
    box-sizing: border-box;
    padding: 0.5em 0.75em;
    border-radius: 4px;
    background: ${({ $colour }) => $colour};
    color: ${({ $text }) => $text};
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    font-size: 0.78rem;
    line-height: 1.3;
    letter-spacing: 0.02em;
`;

export default function ProcessFlow({ config = PROCESS_FLOW_CONFIG }: { config?: ProcessFlowConfig }) {
    const [rect, setWrapperRef] = useBoundingClientRect<HTMLDivElement>();
    const containerWidth = rect?.width ?? 0;
    const { colour } = useTheme();

    const graph = useMemo(() => buildGraph(config, containerWidth, colour.OFF_WHITE), [config, containerWidth, colour.OFF_WHITE]);
    const [nodes, setNodes, onNodesChange] = useNodesState(graph.nodes);
    const fits = graphFits(config, containerWidth);

    // re-seed positions when the container resizes, discarding any dragging the visitor did
    useEffect(() => {
        setNodes(graph.nodes);
    }, [graph.nodes, setNodes]);

    if (!fits) {
        const depth = stageDepths();
        const maxDepth = Math.max(...Object.values(depth));

        return (
            <Layout>
                <Measured ref={setWrapperRef}>
                    <LaneList>
                        {LANE_ORDER.map((lane) => (
                            <Lane key={lane}>
                                <LaneName $colour={LANE_COLOURS[lane]}>{lane}</LaneName>
                                <LaneStages>
                                    {STAGES.filter((stage) => stage.lane === lane).map((stage) => {
                                        const colour = paletteColour(config, stage, depth[stage.id], maxDepth);
                                        return (
                                            <StageChip
                                                key={stage.id}
                                                data-physics
                                                $colour={colour}
                                                $text={textColour(config.nodeStyle, colour)}
                                            >
                                                {stage.label}
                                            </StageChip>
                                        );
                                    })}
                                </LaneStages>
                            </Lane>
                        ))}
                    </LaneList>
                </Measured>
            </Layout>
        );
    }

    return (
        <Layout>
            <Measured ref={setWrapperRef}>
                <Frame $height={graph.height}>
                    <ReactFlow
                        nodes={nodes}
                        onNodesChange={onNodesChange}
                        edges={graph.edges}
                        nodeTypes={NODE_TYPES}
                        nodesDraggable
                        nodesConnectable={false}
                        elementsSelectable={false}
                        zoomOnScroll={false}
                        zoomOnPinch={false}
                        zoomOnDoubleClick={false}
                        panOnScroll={false}
                        panOnDrag={false}
                        autoPanOnNodeDrag={false}
                        preventScrolling={false}
                        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
                        proOptions={{ hideAttribution: true }}
                        style={{ width: '100%', height: '100%', background: colour.DARK_GREY }}
                    >
                        {config.background !== 'none' && (
                            <Background
                                variant={config.background === 'dots' ? BackgroundVariant.Dots : BackgroundVariant.Lines}
                                gap={config.background === 'dots' ? 18 : 40}
                                size={1}
                                color='rgba(232, 232, 232, 0.10)'
                            />
                        )}
                    </ReactFlow>
                </Frame>
            </Measured>
        </Layout>
    );
}
